/**
 * CR-5275: Per-role MFA toggle inside Admin Setup → Security.
 *
 * Lists every Role and lets admins flip ``mfa_required``. Users with a
 * role flagged ``mfa_required`` are forced through TOTP setup on next
 * login and prompted for a 6-digit code on every subsequent login.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../ui/table';
import { coreAPI, type Role } from '../../../lib/api';

export function MfaSettingsCard() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await coreAPI.getRoles({ page_size: 100 });
      // Sort by company_type then name so customer + supplier blocks
      // group together — easier to scan when toggling.
      const sorted = [...res.data.results].sort(
        (a, b) => (
          a.company_type.localeCompare(b.company_type) || a.name.localeCompare(b.name)
        ),
      );
      setRoles(sorted);
    } catch (err: unknown) {
      console.error('Failed to load roles:', err);
      toast.error('Could not load roles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleToggle = async (role: Role, next: boolean) => {
    setUpdatingId(role.id);
    // Optimistic update so the switch feels instant; revert on failure.
    setRoles(prev => prev.map(r => r.id === role.id ? { ...r, mfa_required: next } : r));
    try {
      await coreAPI.updateRole(role.id, { mfa_required: next });
      toast.success(
        next
          ? `MFA is now required for ${role.name}`
          : `MFA is now optional for ${role.name}`,
      );
    } catch (err: unknown) {
      console.error('Failed to update role MFA flag:', err);
      toast.error('Could not update role. Try again.');
      setRoles(prev => prev.map(r => r.id === role.id ? { ...r, mfa_required: !next } : r));
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          Multi-Factor Authentication (MFA)
        </CardTitle>
        <CardDescription>
          Choose which roles must use a TOTP authenticator app (Google Authenticator, Authy,
          1Password, etc.) on top of their password. Users in a required-MFA role are walked
          through setup on their next login and prompted for a 6-digit code on every login
          after that. Superusers are exempt so platform support can always reach the system.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User Role</TableHead>
                <TableHead>Portal</TableHead>
                <TableHead>Department</TableHead>
                <TableHead className="text-right">MFA required</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map(role => (
                <TableRow key={role.id}>
                  <TableCell className="font-medium">
                    {role.name}
                    {role.is_admin_role && (
                      <Badge variant="secondary" className="ml-2">Admin</Badge>
                    )}
                  </TableCell>
                  <TableCell className="capitalize">{role.company_type}</TableCell>
                  <TableCell>{role.department_display}</TableCell>
                  <TableCell className="text-right">
                    <Switch
                      checked={role.mfa_required}
                      onCheckedChange={(next) => handleToggle(role, next)}
                      disabled={updatingId === role.id}
                      aria-label={`Require MFA for ${role.name}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {roles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                    No roles found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default MfaSettingsCard;
